import React from 'react';
import { ArrowRight, Award, Users, Clock } from 'lucide-react';

const Hero: React.FC = () => {
  return (
    <section id="accueil" className="relative bg-gradient-to-br from-stone-50 to-sage-50 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-stone-100/20 to-wood-100/20"></div>
      </div>

      <div className="container-max section-padding relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
          {/* Contenu texte */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight">
                <span className="text-wood-600">Jérémy ARS</span>,<br />
                votre artisan menuisier
              </h1>
              <p className="text-xl md:text-2xl text-stone-600 leading-relaxed">
                Des agencements uniques dans le <strong className="text-sage-700">Morbihan</strong>
              </p>
            </div>

            <p className="text-lg text-stone-600 leading-relaxed">
              Spécialisé en agencement intérieur et extérieur, je réalise vos projets sur mesure
              avec passion et savoir-faire. Du mobilier personnalisé aux terrasses en bois,
              chaque création reflète votre style et s'harmonise avec votre environnement.
            </p>

            {/* Services principaux */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-2">Agencement Intérieur</h3>
                <p className="text-sm text-stone-600">Cuisines, dressings, bibliothèques</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-2">Terrasses & Extérieur</h3>
                <p className="text-sm text-stone-600">Pergolas, terrasses bois/composite</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-2">Menuiseries sur mesure</h3>
                <p className="text-sm text-stone-600">Volets, fenêtres, portails</p>
              </div>
            </div>

            {/* Call to action */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                className="btn-primary flex items-center justify-center space-x-2"
                onClick={() => {
                  const section = document.getElementById('contact');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span>Demander un devis gratuit</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                className="btn-secondary"
                onClick={() => {
                  const section = document.getElementById('realisations');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Voir nos réalisations
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-stone-200">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-wood-100 rounded-full mx-auto mb-2">
                  <Award className="w-6 h-6 text-wood-600" />
                </div>
                <div className="text-2xl font-bold text-stone-900">15+</div>
                <div className="text-sm text-stone-600">Années d'expérience</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-sage-100 rounded-full mx-auto mb-2">
                  <Users className="w-6 h-6 text-sage-600" />
                </div>
                <div className="text-2xl font-bold text-stone-900">200+</div>
                <div className="text-sm text-stone-600">Projets réalisés</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mx-auto mb-2">
                  <Clock className="w-6 h-6 text-slate-600" />
                </div>
                <div className="text-2xl font-bold text-stone-900">7j/7</div>
                <div className="text-sm text-stone-600">Service client</div>
              </div>
            </div>
          </div>

          {/* Image/Visual */}
          <div className="relative animate-slide-up">
            <div className="aspect-square bg-gradient-to-br from-stone-50 via-wood-50 to-sage-50 rounded-2xl shadow-2xl overflow-hidden border-2 border-stone-300" style={{backgroundColor: '#f8f9fa', backgroundImage: 'linear-gradient(to bottom right, #f8f9fa, #f8f3e8, #f6f7f6)'}}>
              {/* Logo principal */}
              <div className="w-full h-full flex items-center justify-center text-wood-700 p-8">
                <div className="text-center space-y-8">
                  {/* Logo galet agrandi */}
                  <div className="w-72 h-48 mx-auto relative">
                    <img 
                      src="/images/ars-agencement.png" 
                      alt="ARS Agencement - Galet avec plan architectural"
                      className="w-full h-full object-contain filter drop-shadow-lg"
                      onError={(e) => {
                        // Fallback design si l'image n'est pas disponible
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    {/* Fallback design temporaire */}
                    <div className="hidden w-full h-full bg-gradient-to-br from-wood-200 to-wood-400 rounded-full flex items-center justify-center shadow-xl">
                      <div className="w-3/4 h-3/4 border-4 border-wood-700 rounded-lg opacity-80 flex items-center justify-center bg-wood-100">
                        <div className="text-wood-800 font-bold text-2xl">ARS</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-xl font-bold text-wood-800">Savoir-faire artisanal</p>
                    <p className="text-lg text-stone-600">Plans sur mesure • Morbihan</p>
                    <p className="text-sm text-stone-500 italic">Chaque projet unique comme un galet du Golfe</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Badge flottant */}
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-stone-200">
              <div className="text-sm text-stone-600">Zone d'intervention</div>
              <div className="font-bold text-stone-900">Morbihan (56)</div>
              <div className="text-xs text-sage-600">Focus Vannes & Golfe du Morbihan</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
